
import React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, useParams } from "react-router-dom";
import { ErrorBoundary } from "@/shared/ui/ErrorBoundary";
import { ErrorState } from "@/shared/ui/states/ErrorState";
import { LoadingState } from "@/shared/ui/states/LoadingState";
import { ProtectedRoute } from "@/shared/components/ProtectedRoute";
import { AuthWrapper } from "@/app/AuthWrapper";
import ProvidersPage from "@/features/providers/components/ProvidersPage";
import { PERMISSIONS } from "@/shared/constants/permissions";
import { usePermissions } from "@/shared/hooks/usePermissions";
import type { AppLayoutProps } from "./types";

// Lazy load admin features
const ClientsPage = React.lazy(() => import("@/features/admin/components/ClientsPage"));
const DriversPage = React.lazy(() => import("@/features/admin/pages/drivers/DriversPage"));
const DriverDetailsPage = React.lazy(
  () => import("@/features/admin/pages/drivers/DriverDetailsPage")
);
const ClientDetailsPage = React.lazy(() => import("@/features/admin/components/ClientDetailsPage"));
const ClientReimbursementDetailsPage = React.lazy(
  () => import("@/features/admin/pages/ClientReimbursementDetailsPage")
);
const NewReimbursementPage = React.lazy(
  () => import("@/features/admin/components/clients/NewReimbursementPage")
);
const TemplateRoutes = React.lazy(() => import("@/features/admin/components/TemplateRoutes.tsx"));
const RunPools = React.lazy(() => import("@/features/admin/components/RunPools.tsx"));
const PrintManifest = React.lazy(() => import("@/pages/PrintManifest"));
const RunPoolDetails = React.lazy(() => import("@/features/admin/components/RunPoolDetails"));
const Runs = React.lazy(() => import("@/features/admin/components/Runs"));
const RunDetails = React.lazy(() => import("@/features/admin/components/RunDetails"));
const FundingAndBilling = React.lazy(
  () => import("@/features/admin/components/FundingAndBilling.tsx")
);
const BillingCodesPage = React.lazy(
  () => import("@/features/admin/components/fundingAndBilling/BillingCodes")
);
const BillingCodeSetsPage = React.lazy(
  () => import("@/features/admin/pages/billingCodeSets/BillingCodeSetsPage")
);
const BillingCodeSetDetailsPage = React.lazy(
  () => import("@/features/admin/components/fundingAndBilling/BillingCodeSetDetailsPage")
);
const BillingCodeDetailsPage = React.lazy(
  () => import("@/features/admin/components/fundingAndBilling/BillingCodeDetailsPage")
);
const BillingCodeHistoryPage = React.lazy(
  () => import("@/features/admin/components/fundingAndBilling/BillingCodeHistoryPage")
);
const BillingCodeSetHistoryPage = React.lazy(
  () => import("@/features/admin/components/fundingAndBilling/BillingCodeSetHistoryPage")
);
const ClientBalanceListPage = React.lazy(
  () => import("@/features/admin/components/clients/ClientBalanceListPage")
);
const ClientBalancePage = React.lazy(
  () => import("@/features/admin/components/clients/ClientBalancePage")
);
const ClientExternalPaymentsPage = React.lazy(
  () => import("@/features/admin/components/clients/ClientExternalPaymentsPage")
);
const ClientHistoryPage = React.lazy(
  () => import("@/features/admin/components/clients/ClientHistoryPage")
);
const FundingSourceEligibilityDetailsPage = React.lazy(
  () => import("@/features/admin/pages/FundingSourceEligibilityDetailsPage")
);
const ClientTripDetailsPage = React.lazy(
  () => import("@/features/admin/pages/ClientTripDetailsPage")
);
const RebookTripPage = React.lazy(
  () => import("@/features/admin/pages/RebookTripPage")
);
const RebookArchiveTripPage = React.lazy(
  () => import("@/features/operations/pages/RebookArchiveTripPage")
);
const NewSubscription = React.lazy(
  () => import("@/features/admin/components/clients/subscriptions/NewSubscription")
);
const ConfirmationSubscriptionPage = React.lazy(
  () => import("@/features/admin/components/clients/subscriptions/ConfirmationSubscriptionPage")
);
const CopyReturnSubscriptionPage = React.lazy(
  () => import("@/features/admin/components/clients/subscriptions/CopyReturnSubscriptionPage")
);
const VehiclesPage = React.lazy(() => import("@/features/admin/components/vehicles/VehiclesPage"));
const VehicleDetailsPage = React.lazy(
  () => import("@/features/admin/components/vehicles/details/VehicleDetailsPage")
);
const ProviderDetailsPage = React.lazy(
  () => import("@/features/providers/components/ProviderDetailsPage")
);
const ProviderRatesPage = React.lazy(
  () => import("@/features/provider-rates/components/ProviderRatesPage")
);
const ProviderRateDetailsPage = React.lazy(
  () => import("@/features/provider-rates/components/ProviderRateDetailsPage")
);
const CopyProviderRatePage = React.lazy(
  () => import("@/features/provider-rates/pages/CopyProviderRatePage")
);
const SurchargesPage = React.lazy(() => import("@/features/admin/pages/surcharges/SurchargesPage"));
const SurchargeDetailsPage = React.lazy(() => import("@/features/admin/pages/surcharges/SurchargeDetailsPage"));
const InspectionsPage = React.lazy(
  () => import("@/features/admin/pages/inspections/InspectionsPage")
);
const InspectionCopyPage = React.lazy(
  () => import("@/features/admin/pages/inspections/InspectionCopyPage")
);
const InspectionDetailsPage = React.lazy(
  () => import("@/features/admin/pages/inspections/InspectionDetailsPage")
);
const ProviderInspectionFormsPage = React.lazy(
  () => import("@/features/admin/pages/inspections/ProviderInspectionFormsPage")
);
const VehicleInspectionFormsPage = React.lazy(
  () => import("@/features/admin/pages/inspections/VehicleInspectionFormsPage")
);
const FundingSourcesPage = React.lazy(
  () => import("@/features/admin/pages/fundingSources/FundingSourcesPage")
);
const FundingSourcesDetails = React.lazy(
  () => import("@/features/admin/pages/fundingSources/FundingSourcesDetails")
);
const ServiceRules = React.lazy(() => import("@/features/admin/pages/fundingSources/ServiceRules"));
const TripPurposePage = React.lazy(
  () => import("@/features/admin/components/tripPurpose/TripPurposePage")
);
const ProviderDenyListPage = React.lazy(
  () => import("@/features/admin/components/providerDenyList/ProviderDenyListPage")
);
const CalendarsPage = React.lazy(() => import("@/features/admin/pages/calendars/CalendarsPage"));
const CalendarDetailsPage = React.lazy(
  () => import("@/features/admin/pages/calendars/CalendarDetailsPage")
);
const SelfServiceAccountsPage = React.lazy(
  () => import("@/features/admin/pages/selfServiceAccounts/SelfServiceAccountsPage")
);
const SelfServiceAccountDetailsPage = React.lazy(
  () => import("@/features/admin/pages/selfServiceAccounts/SelfServiceAccountDetailsPage")
);
const SelfServiceAccountEditPage = React.lazy(
  () => import("@/features/admin/pages/selfServiceAccounts/SelfServiceAccountEditPage")
);
const TripGroupsPage = React.lazy(() => import("@/features/trip-groups/components/TripGroupsPage"));
const TripGroupDetailsPage = React.lazy(
  () => import("@/features/trip-groups/components/TripGroupDetailsPage")
);
const TripGroupHistoryPage = React.lazy(
  () => import("@/features/trip-groups/components/TripGroupHistoryPage")
);
const FixedRoutesPage = React.lazy(
  () => import("@/features/fixed-routes/components/FixedRoutesPage")
);
const FixedRouteDetailsPage = React.lazy(
  () => import("@/features/fixed-routes/components/FixedRouteDetailsPage")
);
const FixedRouteStopsPage = React.lazy(
  () => import("@/features/fixed-routes/components/FixedRouteStopsPage")
);

// Redirect component for old driver history URL -> driver details page
const DriverHistoryRedirect: React.FC = () => {
  const { driverId } = useParams<{ driverId: string }>();
  return <Navigate to={`/admin/drivers/${driverId}`} replace />;
};

// Redirect component for old funding source history URL -> funding source details page.
// Only carries ?tab=history forward when the user has FUNDING_SOURCE_UPDATE; otherwise
// strips the param and lands on the plain details page.  This prevents an unauthorized
// user with a bookmarked /history URL from reaching the history tab content.
const FundingSourceHistoryRedirect: React.FC = () => {
  const { fundingSourceId } = useParams<{ fundingSourceId: string }>();
  const { hasPermission } = usePermissions();
  const canViewHistory = hasPermission(PERMISSIONS.FUNDING_SOURCE_UPDATE);
  const target = canViewHistory
    ? `/admin/funding-sources/${fundingSourceId}?tab=history`
    : `/admin/funding-sources/${fundingSourceId}`;
  return <Navigate to={target} replace />;
};

export const adminRoutes = (AppLayout: React.FC<AppLayoutProps>): RouteObject[] => [
  {
    path: "/admin/clients",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CUSTOMER_VIEW}>
          <ClientsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/drivers",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_VIEW}>
          <DriversPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/drivers/:driverId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_VIEW}>
          <DriverDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/drivers/:driverId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.DRIVER_VIEW}>
          <DriverHistoryRedirect />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/clients/:clientId",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <ClientDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/clients/:clientId/reimbursements/new",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <NewReimbursementPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/clients/:clientId/reimbursements/:reimbursementId",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <ClientReimbursementDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/clients/:clientId/subscriptions/new",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <NewSubscription />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Subscription confirmation page — shown after a successful create or edit.
    // No permission guard required: any dispatcher who just completed the action
    // should always be able to see the confirmation.
    path: "/admin/clients/:clientId/subscriptions/confirmation",
    element: (
      <AppLayout>
        <ConfirmationSubscriptionPage />
      </AppLayout>
    ),
  },
  {
    path: "/admin/clients/:clientId/funding-eligibility/:customerDecisionId",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.CUSTOMER_VIEW}
        >
          <FundingSourceEligibilityDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trips/:tripId",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.TRIP_VIEW}
        >
          <ClientTripDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trips/:tripId/rebook",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.TRIP_CREATE}
        >
          <RebookTripPage actionType="rebook" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trips/:tripId/copy",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.TRIP_CREATE}
        >
          <RebookTripPage actionType="copy" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trips/:tripId/return",
    element: (
      <AppLayout>
        <ProtectedRoute
          apiPermission={PERMISSIONS.TRIP_CREATE}
        >
          <RebookTripPage actionType="return" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trips/:tripId/archive-copy",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.ARCHIVE_TRIP_CREATE}>
          <RebookArchiveTripPage actionType="copy" />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/vehicles",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.VEHICLE_VIEW}>
          <VehiclesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/vehicles/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.VEHICLE_VIEW}>
          <VehicleDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/template-routes",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TEMPLATE_ROUTE_VIEW}>
          <TemplateRoutes />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/runs",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RUN_VIEW}>
          <Runs />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/runs/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RUN_VIEW}>
          <RunDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    // Print manifest — standalone full-page print view (no nav/sidebar), opens in new tab
    path: "/admin/runs/:id/print-manifest",
    element: (
      <ErrorBoundary fallback={ErrorState}>
        <AuthWrapper>
          <React.Suspense fallback={<LoadingState message="Loading manifest..." />}>
            <ProtectedRoute apiPermission={PERMISSIONS.RUN_VIEW}>
              <PrintManifest />
            </ProtectedRoute>
          </React.Suspense>
        </AuthWrapper>
      </ErrorBoundary>
    ),
  },
  {
    // Legacy Rails print manifest URL — handled by the same React component.
    // Uses the print-clean wrapper (no nav/sidebar) like the new route.
    path: "/drt/1/pages/print_manifests/:runId/daily_manifest",
    element: (
      <ErrorBoundary fallback={ErrorState}>
        <AuthWrapper>
          <React.Suspense fallback={<LoadingState message="Loading manifest..." />}>
            <ProtectedRoute apiPermission={PERMISSIONS.RUN_VIEW}>
              <PrintManifest />
            </ProtectedRoute>
          </React.Suspense>
        </AuthWrapper>
      </ErrorBoundary>
    ),
  },
  {
    path: "/admin/run-pools",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RUN_POOL_VIEW}>
          <RunPools />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/run-pools/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.RUN_POOL_VIEW}>
          <RunPoolDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/fundingAndBilling",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BILLING_CODE_SET_VIEW}>
          <FundingAndBilling />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-codes",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FARE_BILLING_CODE_VIEW}>
          <BillingCodesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-codes/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FARE_BILLING_CODE_VIEW}>
          <BillingCodeDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-codes/:id/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FARE_BILLING_CODE_UPDATE}>
          <BillingCodeHistoryPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-code-sets",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BILLING_CODE_SET_VIEW}>
          <BillingCodeSetsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-code-sets/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BILLING_CODE_SET_VIEW}>
          <BillingCodeSetDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/billing-code-sets/:id/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.BILLING_CODE_SET_UPDATE}>
          <BillingCodeSetHistoryPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/surcharges",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SURCHARGE_VIEW}>
          <SurchargesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/surcharges/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SURCHARGE_VIEW}>
          <SurchargeDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/inspections",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW}>
          <InspectionsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/inspections/:inspectionId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW}>
          <InspectionDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/inspections/copy",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW}>
          <InspectionCopyPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/inspections/provider-forms",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW}>
          <ProviderInspectionFormsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/inspections/vehicle-forms",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.INSPECTION_CHECKLIST_TEMPLATE_VIEW}>
          <VehicleInspectionFormsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/funding-sources",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FUNDING_SOURCE_VIEW}>
          <FundingSourcesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/funding-sources/:fundingSourceId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FUNDING_SOURCE_VIEW}>
          <FundingSourcesDetails />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/funding-sources/:fundingSourceId/service-rules",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FUNDING_SOURCE_VIEW}>
          <ServiceRules />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/funding-sources/:fundingSourceId/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.FUNDING_SOURCE_VIEW}>
          <FundingSourceHistoryRedirect />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/self-service-accounts",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SELFSERVICE_USER_VIEW}>
          <SelfServiceAccountsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/self-service-accounts/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.SELFSERVICE_USER_VIEW}>
          <SelfServiceAccountDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/self-service-accounts/:id/edit",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.USER_UPDATE}>
          <SelfServiceAccountEditPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trip-purpose",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.PURPOSE_VIEW}>
          <TripPurposePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider-deny-list",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.COMPANY_BLACKLIST_VIEW}>
          <ProviderDenyListPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trip-groups",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_GROUP_VIEW}>
          <TripGroupsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/trip-groups/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TRIP_GROUP_VIEW}>
          <TripGroupDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
   {
     path: "/admin/trip-groups/:id/subscriptions",
     element: (
       <AppLayout>
         <ProtectedRoute apiPermission={PERMISSIONS.TRIP_GROUP_VIEW}>
           <TripGroupDetailsPage />
         </ProtectedRoute>
       </AppLayout>
     ),
   },
   {
     path: "/admin/trip-groups/:id/trips",
     element: (
       <AppLayout>
         <ProtectedRoute apiPermission={PERMISSIONS.TRIP_GROUP_VIEW}>
           <TripGroupDetailsPage />
         </ProtectedRoute>
       </AppLayout>
     ),
   },
   {
     path: "/admin/trip-groups/:id/history",
     element: (
       <AppLayout>
         <ProtectedRoute apiPermission={PERMISSIONS.TRIP_GROUP_UPDATE}>
           <TripGroupHistoryPage />
         </ProtectedRoute>
       </AppLayout>
     ),
   },
  {
    path: "/admin/fixed-routes",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TEMPLATE_ROUTE_VIEW}>
          <FixedRoutesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/fixed-routes/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TEMPLATE_ROUTE_VIEW}>
          <FixedRouteDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/fixed-routes/:id/stops",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.TEMPLATE_ROUTE_VIEW}>
          <FixedRouteStopsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/providers",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.COMPANY_VIEW}>
          <ProvidersPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider/:providerId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.COMPANY_VIEW}>
          <ProviderDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider-rates",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.PROVIDER_RATE_VIEW}>
          <ProviderRatesPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider-rates/:id",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.PROVIDER_RATE_VIEW}>
          <ProviderRateDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider-rates/:id/copy",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.PROVIDER_RATE_CREATE}>
          <CopyProviderRatePage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/provider-rates/:id/history",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.PROVIDER_RATE_UPDATE}>
          <ProviderRateDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/calendars",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CALENDAR_VIEW}>
          <CalendarsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
  {
    path: "/admin/calendars/:calendarId",
    element: (
      <AppLayout>
        <ProtectedRoute apiPermission={PERMISSIONS.CALENDAR_VIEW}>
          <CalendarDetailsPage />
        </ProtectedRoute>
      </AppLayout>
    ),
  },
];
